import { useFormContext } from 'react-hook-form/dist/index.ie11';
import { generateMealCreditData } from 'src/components/oneCorp/features/MealCredit/components/Form/hooks/useMealCreditDataHelpers';
import {
  defaultFormValues,
  IMealCreditFormFields,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { getFulfillmentTypes } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getFulfillmentTypes';
import { CorpUIResponse } from 'src/types/Corp';
import {
  transformPayloadToFormMerchantRestrictions,
  transformPayloadToFormItemRestrictions,
} from 'src/common/OrderRestrictions/orderRestrictions.utils';

export interface UseMealCreditData {
  setFormFieldsWithMealCreditData: (mealCredit: IMealCredit) => void;
}

export interface useMealCreditDataProps {
  corpData: CorpUIResponse;
}

export const useMealCreditData = ({
  corpData,
}: useMealCreditDataProps): UseMealCreditData => {
  const { reset, setValue, trigger } = useFormContext<IMealCreditFormFields>();

  function setFormFieldsWithMealCreditData(mealCredit: IMealCredit): void {
    if (!mealCredit || !corpData) {
      return;
    }

    reset({
      ...defaultFormValues,
      merchantRestrictions: transformPayloadToFormMerchantRestrictions(
        mealCredit.merchant_restrictions
      ),
      itemRestrictions: transformPayloadToFormItemRestrictions(
        mealCredit.item_restrictions
      ),
      ...generateMealCreditData({
        mealCredit,
        corpData,
      }),
    });

    updateFormFields(mealCredit);
  }

  /**
   * This function is responsible for updating the form fields with the given meal credit data.
   * Some values in the form depend on others, therefore we need to:
   * 1. Reset the form to initial values.
   * 2. Set new values for the dependent fields.
   * 3. This sequence ensures all dependent values are refreshed correctly.
   */
  async function updateFormFields(mealCredit: IMealCredit): Promise<void> {
    await trigger();

    setValue(
      'fulfillmentType',
      getFulfillmentTypes(mealCredit.fulfillment_types)
    );
  }

  return {
    setFormFieldsWithMealCreditData,
  };
};
